// Error structure types for API responses (Legacy format)
export interface ApiErrorResponse {
  status: string;
  error: {
    code: string;
    message: string;
    details?: string;
    statusCode: number;
    domain?: string; // New: domain field for error categorization
    field?: string;  // New: field name if error is field-specific
  };
  timestamp: string;
  path: string;
  method: string;
}

// New domain-based error format
export interface DomainBasedErrorResponse {
  code: string;           // Error code (e.g., "CLIENT_NOT_FOUND_OR_INACTIVE")
  message: string;        // Human-readable message from backend
  domain: string;         // Error domain (e.g., "auth", "validation", "general")
  statusCode: number;     // HTTP status code
  details?: string;       // Additional error details
  field?: string;         // Field name if validation error
  timestamp?: string;     // Error timestamp
  path?: string;          // Request path
  method?: string;        // HTTP method
}

// Specific error types for different endpoints
export interface AuthErrorResponse extends ApiErrorResponse {
}

// Error codes mapping
export const ERROR_CODES = {
  // Authentication errors
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  VALIDATION_ERROR: 422,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  BAD_REQUEST: 400,
} as const;

// Error message codes mapping
export const ERROR_MESSAGE_CODES = {
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',
  PASSWORD_TOO_SHORT: 'PASSWORD_TOO_SHORT',
  INVALID_EMAIL_FORMAT: 'INVALID_EMAIL_FORMAT',
} as const;

// Error messages mapping
export const ERROR_MESSAGES = {
  [ERROR_CODES.UNAUTHORIZED]: 'Unauthorized access. Please login again.',
  [ERROR_CODES.FORBIDDEN]: 'Access forbidden. Insufficient permissions.',
  [ERROR_CODES.NOT_FOUND]: 'Resource not found.',
  [ERROR_CODES.VALIDATION_ERROR]: 'Validation error occurred.',
  [ERROR_CODES.INTERNAL_SERVER_ERROR]: 'Internal server error. Please try again later.',
  [ERROR_CODES.BAD_GATEWAY]: 'Bad gateway. Please try again later.',
  [ERROR_CODES.SERVICE_UNAVAILABLE]: 'Service temporarily unavailable.',
  [ERROR_CODES.BAD_REQUEST]: 'Bad request. Please check your input.',
} as const;

// Specific error message mapping - using i18n translation keys (namespace: 'error')
export const SPECIFIC_ERROR_MESSAGES = {
  [ERROR_MESSAGE_CODES.USER_NOT_FOUND]: 'check-credentials',
  [ERROR_MESSAGE_CODES.INVALID_CREDENTIALS]: 'check-credentials',
  [ERROR_MESSAGE_CODES.VALIDATION_FAILED]: 'Please check your input data.',
  [ERROR_MESSAGE_CODES.EMAIL_ALREADY_EXISTS]: 'Email already exists.',
  [ERROR_MESSAGE_CODES.PASSWORD_TOO_SHORT]: 'Password is too short.',
  [ERROR_MESSAGE_CODES.INVALID_EMAIL_FORMAT]: 'Invalid email format.',
} as const;

// Error handler function types
export type ErrorHandler = (error: ApiErrorResponse) => void;
export type AuthErrorHandler = (error: AuthErrorResponse) => void;

// Error structure for specific endpoints
export const ENDPOINT_ERROR_STRUCTURES = {
  'auth/sign-in': {
    errorCodes: [ERROR_CODES.UNAUTHORIZED, ERROR_CODES.VALIDATION_ERROR, ERROR_CODES.INTERNAL_SERVER_ERROR, ERROR_CODES.BAD_REQUEST],
    specificHandling: true,
    retryable: false,
  },
  'auth/sign-up': {
    errorCodes: [ERROR_CODES.VALIDATION_ERROR, ERROR_CODES.INTERNAL_SERVER_ERROR, ERROR_CODES.BAD_REQUEST],
    specificHandling: true,
    retryable: false,
  },
  'auth/logout': {
    errorCodes: [ERROR_CODES.UNAUTHORIZED, ERROR_CODES.INTERNAL_SERVER_ERROR],
    specificHandling: false,
    retryable: false,
  },
} as const;

// Helper function to get user-friendly error message
export const getUserFriendlyMessage = (error: ApiErrorResponse | DomainBasedErrorResponse): string => {
  // Handle new domain-based format
  if ('domain' in error) {
    const domainError = error as DomainBasedErrorResponse;
    return domainError.message || domainError.details || 'An unexpected error occurred.';
  }
  
  // Handle legacy format
  const legacyError = error as ApiErrorResponse;
  
  // Check for specific error code first
  if (legacyError.error.code && SPECIFIC_ERROR_MESSAGES[legacyError.error.code as keyof typeof SPECIFIC_ERROR_MESSAGES]) {
    return SPECIFIC_ERROR_MESSAGES[legacyError.error.code as keyof typeof SPECIFIC_ERROR_MESSAGES];
  }
  
  // Check for error message
  if (legacyError.error.message) {
    return legacyError.error.message;
  }
  
  // Check for error details
  if (legacyError.error.details) {
    return legacyError.error.details;
  }
  
  // Fallback to status-based message
  return ERROR_MESSAGES[legacyError.error.statusCode as keyof typeof ERROR_MESSAGES] || 'An unexpected error occurred.';
};

// Helper to check if error is in new domain-based format
export const isDomainBasedError = (error: any): error is DomainBasedErrorResponse => {
  return error && typeof error === 'object' && 'domain' in error && 'code' in error;
};
